import React, { useState } from "react";
import MarketingNav from "@/components/MarketingNav";
import QuoteCTA from "@/components/QuoteCTA";
import Seo from "@/components/Seo";
import SiteFooter from "@/components/SiteFooter";
import { BIZ } from "@/data/site";
import { Plus, Minus } from "lucide-react";

const FAQS = [
  { q: "Do you offer free estimates?", a: "Yes. Every quote is free and comes in writing. Use our online estimate calculator for a preliminary range, or call/text (615) 519-2629 for a full written quote." },
  { q: "How is pricing determined?", a: "We price based on measured surface area (square feet or linear feet), material type, condition of the surface, access, height, water availability, and any add-ons. Starting prices are visible on every service page." },
  { q: "Is there a minimum service fee?", a: "Each service has its own starting price (e.g. Sidewalk from $99, House Wash from $279). We do not apply a separate blanket minimum on top of those." },
  { q: "What payment methods do you accept?", a: "Cash, check, and major credit/debit cards on the day of service. Commercial invoicing is available for recurring customers." },
  { q: "What is soft washing?", a: "Soft washing uses low-pressure equipment and a professional-grade detergent to clean delicate surfaces (siding, roofs, painted trim) without driving water behind seams. It is the correct method for exteriors and roofs — high pressure is not." },
  { q: "Is pressure washing safe for my roof?", a: "No — high pressure should never be used on asphalt shingles. It voids most shingle warranties and dislodges protective granules. We use a low-pressure soft-wash with an appropriate detergent so the algae dies and rinses away without damage." },
  { q: "Will you damage my landscaping?", a: "Sensitive plants get a pre-soak with water before we apply any detergent, and a final rinse afterwards. On a soft-wash we work at low pressure, avoiding direct spray on foliage." },
  { q: "How soon can you come?", a: `We aim for same-week scheduling. Response time to quote requests is typically ${BIZ.responseTime}.` },
  { q: "Do you work weekends?", a: "Yes. We take quote requests 24/7 and schedule service any day of the week that fits your calendar." },
  { q: "Do you serve my city?", a: `We regularly serve ${BIZ.serviceAreas.join(", ")}, and can travel further with a small surcharge. Call to confirm.` },
  { q: "Are you licensed and insured?", a: "Yes. Wynridge Exterior Cleaning carries general liability insurance. A certificate of insurance is available on request for HOAs, property managers, and commercial customers." },
  { q: "How long will results last?", a: "Depends on the surface and local conditions. House and roof washes typically stay clean 1–3 years. Driveways and horizontal concrete build up mildew faster in shaded areas — annual maintenance is common." },
  { q: "What if I'm not satisfied?", a: "We do a walk-through with you before we leave. Anything you point out on that walk, we address on the spot." },
  { q: "Do I need to be home?", a: "No. As long as we have access (gate code, water spigot access, and any special notes), you don't need to be present. We'll photo-report before and after." },
  { q: "Do you need water and power on-site?", a: "We usually need an outdoor water spigot. Power depends on the job — we bring generators when needed. If your property has neither, let us know when you book." },
  { q: "Can you remove every stain?", a: "We cannot promise removal of every oil, rust, or permanent stain from concrete — outcomes depend on stain age, depth, and surface porosity. We'll tell you honestly what we expect before we start." },
  { q: "Do you use eco-friendly detergents?", a: "Standard detergents get the job done. On request we'll switch to a plant-safe biodegradable blend at no extra charge — just let us know when you book." },
  { q: "Do you offer package deals?", a: "Yes — bundle any two services and get 10% off the equal-or-lesser add-on. New customers also get 15% off with code WELCOME15." },
];

export default function FaqPage() {
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: FAQS.map(({ q, a }) => ({
      "@type": "Question",
      name: q,
      acceptedAnswer: { "@type": "Answer", text: a },
    })),
  };

  return (
    <div className="bg-paper min-h-screen">
      <Seo
        title="Frequently Asked Questions"
        description={`Answers to common questions about pressure washing, soft washing, pricing, service areas, and scheduling in ${BIZ.city}, ${BIZ.stateShort}.`}
        path="/faq/"
        jsonLd={jsonLd}
      />
      <MarketingNav />
      <QuoteCTA />

      <section className="pt-6 pb-20 md:pb-24">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <div className="text-xs font-bold uppercase tracking-[0.25em] text-brand-700 mb-4">FAQ</div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tight text-stone-900 leading-[1.05]" style={{ fontFamily: "Outfit" }}>
            Questions we<br /><span className="text-electric-500 italic">get every week.</span>
          </h1>

          <div className="mt-12 space-y-3" data-testid="faq-list">
            {FAQS.map((item, i) => <FaqItem key={i} {...item} index={i} />)}
          </div>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}

function FaqItem({ q, a, index }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-2xl border border-stone-200 bg-white overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid={`faq-toggle-${index}`}
        className="w-full flex items-center justify-between text-left px-5 md:px-6 py-4 hover:bg-stone-50 transition-colors"
      >
        <span className="font-bold text-stone-900 pr-4" style={{ fontFamily: "Outfit" }}>{q}</span>
        {open ? <Minus className="w-4 h-4 text-brand-600 flex-shrink-0" /> : <Plus className="w-4 h-4 text-brand-600 flex-shrink-0" />}
      </button>
      {open && (
        <div className="px-5 md:px-6 pb-5 text-stone-600 leading-relaxed">{a}</div>
      )}
    </div>
  );
}
